import { useState } from 'react';
import { ArrowDownLeft, ArrowUpRight, Paperclip } from 'lucide-react';
import { formatInstant } from '@/lib/date';

export type MessageBodyError =
  | 'not-connected'
  | 'no-read-scope'
  | 'unavailable'
  | 'no-text-part';

export interface EmailMessageData {
  id: string;
  direction: 'inbound' | 'outbound';
  fromAddress: string | null;
  sentAt: string;
  subject: string | null;
  matchTier: number | null;
  snippet: string | null;
  attachments: { filename: string; mimeType: string; size: number }[];
  body: { visible: string; quoted: string } | null;
  bodyError: MessageBodyError | null;
}

/**
 * Why the body is missing decides who can act on it.
 *
 * `not-connected` and `no-read-scope` are account-level, and GmailBanner
 * (rendered globally in Layout) already polls for exactly those and puts the
 * remedy on screen above the thread. Repeating "go to Settings" under every
 * message would state one condition four more times, so these stay terse.
 *
 * `unavailable` and `no-text-part` are per-message and invisible to the
 * banner: a transient fetch failure and an HTML-only message are different
 * problems, so each keeps its own explanation.
 */
function missingBodyNote(bodyError: MessageBodyError | null, outbound: boolean): string {
  switch (bodyError) {
    case 'not-connected':
    case 'no-read-scope':
      return 'Not loaded';
    case 'no-text-part':
      return 'This message is HTML only, with no plain-text version to show.';
    default:
      // Only the fetch-failure case still needs the outbound phrasing: the
      // planner wrote this one, so "couldn't be loaded" would read as their
      // own words having gone missing.
      return outbound
        ? 'You sent this from VendorOps. The copy in Gmail could not be reached just now.'
        : "The full message couldn't be loaded from Gmail.";
  }
}

/**
 * One email, rendered to be read rather than clicked open.
 *
 * Deliberately fetchless: the inquiry page loads a whole thread in one request
 * while the review queue loads a single unmatched message on demand, and both
 * render this.
 */
/**
 * Finds `needle` in `haystack` ignoring how either one wraps, and returns the
 * three pieces to render.
 *
 * The vendor's line breaks are not ours: a quote read as "Our rate is $2000"
 * may sit in the body as "Our rate is\n  $2000". Collapsing whitespace on both
 * sides before searching is the same normalisation `quote-extract.ts` applies
 * when it validates that the model did not invent the figure, so a substring
 * that passed there matches here.
 */
export function splitOnMatch(
  haystack: string,
  needle: string,
): { before: string; match: string; after: string } | null {
  const trimmed = needle.trim();
  if (trimmed.length === 0) return null;

  // Walk the original text, tracking where each non-space character lands in
  // its collapsed form, so a hit in collapsed space maps back to real offsets.
  const offsets: number[] = [];
  let collapsed = '';
  let lastWasSpace = true;
  for (let i = 0; i < haystack.length; i++) {
    const ch = haystack[i]!;
    if (/\s/.test(ch)) {
      if (!lastWasSpace) {
        offsets.push(i);
        collapsed += ' ';
        lastWasSpace = true;
      }
      continue;
    }
    offsets.push(i);
    collapsed += ch;
    lastWasSpace = false;
  }

  const target = trimmed.replace(/\s+/g, ' ');
  const at = collapsed.toLowerCase().indexOf(target.toLowerCase());
  if (at === -1) return null;

  const start = offsets[at]!;
  const endIndex = offsets[at + target.length - 1];
  const end = endIndex === undefined ? haystack.length : endIndex + 1;

  return {
    before: haystack.slice(0, start),
    match: haystack.slice(start, end),
    after: haystack.slice(end),
  };
}

export default function EmailMessage({
  message,
  threadSubject,
  highlight,
  inquiryId,
}: {
  message: EmailMessageData;
  threadSubject: string | null;
  /** Verbatim text to mark in the body — the sentence a quote was read from.
   *  Ignored when it does not appear, so a stale value degrades to no
   *  highlight rather than to a wrong one. */
  highlight?: string;
  /** The inquiry the attachment route hangs off. Optional because the review
   *  queue renders this component for a message that is not filed anywhere
   *  yet — there is no inquiry to address an attachment against, and the
   *  filenames degrade to plain text rather than to broken links. */
  inquiryId?: string;
}) {
  const [showQuoted, setShowQuoted] = useState(false);
  const inbound = message.direction === 'inbound';
  const quoted = message.body?.quoted?.trim() ? message.body.quoted : '';
  const hasSnippet = Boolean(message.snippet?.trim());
  // A vendor changing the subject mid-thread is worth surfacing. The same
  // subject repeated down the page is not.
  const ownSubject = message.subject && message.subject !== threadSubject ? message.subject : null;

  return (
    <article className="py-4">
      <header className="flex items-baseline gap-2">
        {inbound ? (
          <ArrowDownLeft className="size-4 shrink-0 self-center text-primary" />
        ) : (
          <ArrowUpRight className="size-4 shrink-0 self-center text-muted-foreground" />
        )}
        <p className={`text-sm font-medium ${inbound ? '' : 'text-muted-foreground'}`}>
          {inbound ? (message.fromAddress ?? 'Unknown sender') : 'You'}
        </p>
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {formatInstant(message.sentAt, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </span>
        {inbound && message.matchTier && (
          <span
            className="shrink-0 text-xs text-muted-foreground"
            title={`Matched at tier ${message.matchTier}`}
          >
            T{message.matchTier}
          </span>
        )}
      </header>

      {ownSubject && <p className="mt-1 pl-6 text-xs text-muted-foreground">{ownSubject}</p>}

      <div className="mt-2 pl-6">
        {message.body ? (
          <pre
            className={`whitespace-pre-wrap font-sans text-sm ${
              inbound ? '' : 'text-muted-foreground'
            }`}
          >
            {(() => {
              const split = highlight ? splitOnMatch(message.body.visible, highlight) : null;
              if (!split) return message.body.visible;
              return (
                <>
                  {split.before}
                  {/* Tinted from the surface hue rather than the browser's
                      yellow, which belongs to no design system. */}
                  <mark className="rounded-sm bg-[var(--color-tertiary-container)] px-0.5 text-foreground">
                    {split.match}
                  </mark>
                  {split.after}
                </>
              );
            })()}
          </pre>
        ) : (
          <>
            {hasSnippet && (
              <pre className="whitespace-pre-wrap font-sans text-sm text-muted-foreground">
                {message.snippet}
              </pre>
            )}
            <p className={`text-xs text-muted-foreground ${hasSnippet ? 'mt-1' : ''}`}>
              {missingBodyNote(message.bodyError, !inbound)}
            </p>
          </>
        )}

        {quoted && (
          <>
            <button
              aria-label={showQuoted ? 'Hide quoted text' : 'Show quoted text'}
              aria-expanded={showQuoted}
              className="mt-2 rounded-full bg-[var(--color-surface-container-high)] px-2 py-0.5 text-xs leading-none text-muted-foreground hover:text-foreground"
              onClick={() => setShowQuoted((v) => !v)}
            >
              ···
            </button>
            {showQuoted && (
              <pre className="mt-2 whitespace-pre-wrap border-l-2 border-border pl-3 font-sans text-sm text-muted-foreground">
                {quoted}
              </pre>
            )}
          </>
        )}

        {/* The planner's copy of whatever the vendor sent, openable.

            It listed filenames and did nothing with them, which is the wrong
            place to stop: the file under the paperclip is frequently the
            whole point of the message — a proposal, a contract, a rate card
            — and the planner's own next move is to read it. The Quotes tab
            already opened the same PDF, so the thread was the one surface
            that showed the file and refused to hand it over.

            A real anchor rather than a click handler, so cmd-click, middle
            click and "copy link address" all behave the way they do on every
            other link. `noopener` because the target is one planner's vendor
            pricing.

            The index is the attachment's position in *this* list, and the
            route resolves it against `visibleAttachments` of the live parts.
            Both sides run the same filter in the same order — see that
            function's own comment, which exists precisely to keep this
            agreement — so "the second attachment" means one thing to the row
            a planner clicks and to the fetch that answers the click. */}
        {message.attachments.length > 0 && (
          <ul className="mt-3 space-y-1">
            {message.attachments.map((a, i) => {
              const label = (
                <>
                  <Paperclip className="size-3 shrink-0" />
                  {a.filename}
                  <span>· {Math.round(a.size / 1024)} KB</span>
                </>
              );
              return (
                <li key={`${a.filename}-${i}`} className="text-xs text-muted-foreground">
                  {inquiryId ? (
                    <a
                      href={`/api/inquiries/${inquiryId}/messages/${message.id}/attachment/${i}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-sm underline
                        decoration-dotted underline-offset-4 transition-colors
                        hover:text-foreground hover:decoration-solid
                        focus-visible:outline-2 focus-visible:outline-offset-2
                        focus-visible:outline-ring"
                    >
                      {label}
                    </a>
                  ) : (
                    // The review queue renders this with no inquiry: the
                    // message is not filed, so no route could serve it.
                    <span className="inline-flex items-center gap-2">{label}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </article>
  );
}
